import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import Layout from "./Layout.jsx";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useUTMTracking } from "@/hooks/useUTMTracking";
import { useAuth } from "@/contexts/AuthContext";
import Icon from '@/components/ui/Icon';
import { BUILD_TIME_FLAGS } from '@/config/featureFlags';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';

// Lazy load heavy components for better performance
const Dashboard = lazy(() => import("./Dashboard"));
const Discovery = lazy(() => import("./DiscoveryEnhanced"));
const SearchResults = lazy(() => import("./SearchResults"));
const DatabaseSearchResults = lazy(() => import("./DatabaseSearchResults"));
const Calendar = lazy(() => import("./Calendar"));
const MyCircle = lazy(() => import("./MyCircle"));
const Profile = lazy(() => import("./Profile"));
const Landing = lazy(() => import("./Landing"));
const MyPrograms = lazy(() => import("./MyPrograms"));
const Premium = lazy(() => import("./Premium"));
const PremiumStripeTest = lazy(() => import("./PremiumStripeTest"));
const Blog = lazy(() => import("./Blog"));
const BlogArticle = lazy(() => import("./BlogArticle"));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy"));
import TermsOfUse from "./TermsOfUse";
import ErrorBoundary from "@/components/ErrorBoundary";
const PlaceDetailPage = lazy(() => import("./PlaceDetailPage"));
const ProgramDetailPage = lazy(() => import("./ProgramDetailPage"));
const LoginForm = lazy(() => import("@/components/auth/LoginForm"));
const SignupForm = lazy(() => import("@/components/auth/SignupForm"));
const ForgotPasswordForm = lazy(() => import("@/components/auth/ForgotPasswordForm"));
const ResetPasswordForm = lazy(() => import("@/components/auth/ResetPasswordForm"));
const LocationTest = lazy(() => import("@/components/test/LocationTest"));
const SettingsPage = lazy(() => import("./Settings"));
const FAQ = lazy(() => import("./FAQ"));

// Additional imports from main branch
import DiscoveredCampsPage from "./DiscoveredCampsPage";
import AIIconPreview from '@/components/AIIconPreview';
import MyCamps from "./MyCamps";
import OAuthCallback from "./OAuthCallback";
import ContactsOAuthCallback from "./ContactsOAuthCallback";
import TestPage from "./TestPage";
import EmailServiceTest from "./EmailServiceTest";
import EmailProcessingTest from "./EmailProcessingTest";
import OnboardingTest from "./OnboardingTest";
import EmailSettingsTest from "./EmailSettingsTest";
import CampDisplayTest from "./CampDisplayTest";
import N8NWorkflowTester from "@/components/debug/N8NWorkflowTester";
import DebugTools from "./DebugTools";
import AnalyticsTest from "@/components/test/AnalyticsTest";

// Import new dashboard components for testing
import MyCampsDashboardTestPage from '@/components/dashboard/MyCampsDashboardTestPage';

// Import admin components
import AdminDashboard from './AdminDashboard';

// Import Google Places Test component
import GooglePlacesTest from './GooglePlacesTest';
import QuickIntegrationTest from './QuickIntegrationTest';
import IntegrationTestPage from './IntegrationTestPage';
import CacheDiagnostics from './CacheDiagnostics';
import PerfectMatchesPage from './PerfectMatchesPage';
import TestUniversalModal from './TestUniversalModal';
const PollSharePage = lazy(() => import('./PollSharePage'));
const TimelineSharePage = lazy(() => import('./TimelineSharePage'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen bg-gray-100">
    <AppLoadingSpinner message="Loading..." />
  </div>
);

const PAGES = {
    Dashboard: Dashboard,
    Discovery: Discovery,
    Calendar: Calendar,
    MyCircle: MyCircle,
    Profile: Profile,
    Landing: Landing,
    MyPrograms: MyPrograms,
    MyCamps: MyCamps,
    Premium: Premium,
    Settings: SettingsPage,
}

function _getCurrentPage(url) {
    // Skip page detection for auth routes and callback routes
    if (url.includes('/auth/') || url.includes('/callback')) {
        return null;
    }
    
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || Object.keys(PAGES)[0];
}

// Component to handle root route logic
function RootRouteHandler() {
    const { user, loading } = useAuth();
    
    // Show loading spinner while checking authentication
    if (loading) {
        return <LoadingSpinner />;
    }
    
    // If authenticated, redirect to Dashboard route
    if (user) {
        return <Navigate to="/Dashboard" replace />;
    }
    
    // If not authenticated, show Landing page
    return <Landing />;
}

// Create a wrapper component that uses useLocation inside the Router context
function PagesContent() {
    const location = useLocation();
    const currentPage = _getCurrentPage(location.pathname);
    
    // Initialize UTM tracking (must be called before analytics)
    useUTMTracking();
    
    // Automatically track page views on route changes
    useAnalytics();
    
    return (
        <Suspense fallback={<LoadingSpinner />}>
            <Routes>
                {/* Public routes */}
                <Route path="/auth/login" element={<LoginForm />} />
                <Route path="/auth/signup" element={<SignupForm />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordForm />} />
                <Route path="/auth/reset-password" element={<ResetPasswordForm />} />
                <Route path="/auth/google/callback" element={<OAuthCallback />} />
                <Route path="/auth/google/contacts-callback" element={<ContactsOAuthCallback />} />
                <Route path="/auth/test-callback" element={
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <h1>Test Callback Route</h1>
                    <p>This is a test route to verify routing works without authentication.</p>
                    <p>Current URL: {window.location.href}</p>
                  </div>
                } />
                <Route path="/landing" element={<Landing />} />
                
                {/* Public legal pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<ErrorBoundary><TermsOfUse /></ErrorBoundary>} />
                
                {/* FAQ/Help page - accessible to all users */}
                <Route path="/help" element={<FAQ />} />
                <Route path="/faq" element={<FAQ />} />
                
                {/* Poll share page - public route for sharing */}
                {/* Handle both /poll/:pollId and /poll/:pollId/share */}
                <Route path="/poll/:pollId/share" element={<PollSharePage />} />
                <Route path="/poll/:pollId" element={<PollSharePage />} />
                
                {/* Timeline share page - public route for sharing */}
                {/* Handle both /timeline/:userId/:shareToken and /timeline/:userId */}
                <Route path="/timeline/:userId/:shareToken" element={<TimelineSharePage />} />
                <Route path="/timeline/:userId" element={<TimelineSharePage />} />
                
                {/* Premium page - accessible to all users */}
                <Route path="/Premium" element={<Premium />} />
                
                {/* Premium Stripe Test page - accessible to all users */}
                <Route path="/premium-stripe-test" element={<PremiumStripeTest />} />
                
                {/* Blog routes - controlled by feature flag */}
                {BUILD_TIME_FLAGS.blog_page && (
                  <>
                    {/* Blog page - accessible to all users */}
                    <Route path="/blog" element={<Blog />} />
                    
                    {/* Blog article page - accessible to all users */}
                    <Route path="/blog/:slug" element={<BlogArticle />} />
                  </>
                )}
                
                {/* Root route - Landing for unauthenticated, Dashboard for authenticated */}
                <Route path="/" element={<RootRouteHandler />} />
                
                <Route path="/Dashboard" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Dashboard />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/email-scout-details" element={
                    <ProtectedRoute>
                        <DiscoveredCampsPage />
                    </ProtectedRoute>
                } />
                <Route path="/perfect-matches" element={
                    <ProtectedRoute>
                        <PerfectMatchesPage />
                    </ProtectedRoute>
                } />
                
                <Route path="/Discovery" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Discovery />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/discover" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Discovery />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/search-results" element={
                    <ProtectedRoute>
                        <SearchResults />
                    </ProtectedRoute>
                } />
                
                <Route path="/database-search-results" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <DatabaseSearchResults />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/Calendar" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Calendar />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/MyCircle" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <MyCircle />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                
                <Route path="/Profile" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <Profile />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/Settings" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <SettingsPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/settings" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <SettingsPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                <Route path="/MyPrograms" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <MyPrograms />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/MyCamps" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <MyCamps />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/my-camps" element={
                    <ProtectedRoute>
                        <Layout currentPageName={currentPage}>
                            <MyCamps />
                        </Layout>
                    </ProtectedRoute>
                } />
                

                {/* Program Detail Page */}
                <Route path="/program/:id" element={
                    <ProtectedRoute>
                        <ProgramDetailPage />
                    </ProtectedRoute>
                } />

                {/* Place Detail Page */}
                <Route path="/place/:placeId" element={
                    <ProtectedRoute>
                        <PlaceDetailPage />
                    </ProtectedRoute>
                } />

                {/* Admin routes */}
                <Route path="/admin" element={
                    <ProtectedRoute>
                        <AdminDashboard />
                    </ProtectedRoute>
                } />

                {/* Temporary test route for location services */}
                <Route path="/location-test" element={
                    <ProtectedRoute>
                        <Layout currentPageName="Location Test">
                            <LocationTest />
                        </Layout>
                    </ProtectedRoute>
                } />

                {/* Test route for Gmail OAuth component */}
                <Route path="/test" element={<TestPage />} />

                {/* Test route for Email Service */}
                <Route path="/email-test" element={<EmailServiceTest />} />

                {/* Test route for Email Processing */}
                <Route path="/email-processing-test" element={<EmailProcessingTest />} />

                {/* Test route for Onboarding Integration */}
                <Route path="/onboarding-test" element={<OnboardingTest />} />

                {/* Test route for Email Settings */}
                <Route path="/email-settings-test" element={<EmailSettingsTest />} />

                {/* Test route for Camp Display */}
                <Route path="/camp-display-test" element={<CampDisplayTest />} />

                {/* Test route for Universal Program Modal */}
                <Route path="/test-universal-modal" element={
                    <ProtectedRoute>
                        <Layout currentPageName="Test Universal Modal">
                            <TestUniversalModal />
                        </Layout>
                    </ProtectedRoute>
                } />

                {/* Test route for AI Icons Preview */}
                <Route path="/ai-icons-preview" element={
                    <ProtectedRoute>
                        <Layout currentPageName="AI Icons Preview">
                            <AIIconPreview />
                        </Layout>
                    </ProtectedRoute>
                } />

                {/* Test route for N8N Workflow Testing */}
                <Route path="/n8n-workflow-test" element={
                    <ProtectedRoute>
                        <Layout currentPageName="N8N Workflow Test">
                            <N8NWorkflowTester />
                        </Layout>
                    </ProtectedRoute>
                } />

                {/* Debug Tools - Redirect to Admin Dashboard */}
                <Route path="/DebugTools" element={<Navigate to="/admin" replace />} />
                
                {/* Analytics Test - Redirect to Admin Dashboard */}
                <Route path="/analytics-test" element={<Navigate to="/admin" replace />} />

                {/* Test routes for new dashboard components */}
                <Route path="/dashboard-test" element={
                    <ProtectedRoute>
                        <Layout currentPageName="Dashboard Test">
                            <MyCampsDashboardTestPage />
                        </Layout>
                    </ProtectedRoute>
                } />

                <Route path="/camp-details-test" element={
                    <ProtectedRoute>
                        <Layout currentPageName="Camp Details Test">
                            <div className="p-8">
                                <h1 className="text-2xl font-bold">Camp Details Test</h1>
                                <p>This test page has been removed.</p>
                            </div>
                        </Layout>
                    </ProtectedRoute>
                } />

                {/* Analytics Test - Moved to Admin Dashboard */}
                {/* Analytics Test is now available in the Admin Dashboard at /admin */}

                {/* Google Places API Migration Test */}
                <Route path="/google-places-test" element={<GooglePlacesTest />} />

                {/* Quick Integration Test */}
                <Route path="/quick-test" element={
                    <ProtectedRoute>
                        <QuickIntegrationTest />
                    </ProtectedRoute>
                } />

                {/* Full Integration Test Suite */}
                <Route path="/integration-test" element={
                    <ProtectedRoute>
                        <IntegrationTestPage />
                    </ProtectedRoute>
                } />

                {/* Cache Diagnostics */}
                <Route path="/cache-diagnostics" element={
                    <ProtectedRoute>
                        <CacheDiagnostics />
                    </ProtectedRoute>
                } />
            </Routes>
        </Suspense>
    );
}

export default function Pages() {
    return (
        <Router>
            <PagesContent />
        </Router>
    );
}